import React from "react"

import { avatarColorFor, initialsFrom } from "../../lib/avatar"
import { cn } from "../../lib/utils"

/**
 * One person's circle: their colour, their initials, white letters, centred.
 *
 * <p><b>The single renderer for every avatar in this package</b> — participant rows, the add-people
 * picker, message cards and the grid hover preview. Each of those used to build its own span with its
 * own colour and initials logic, and they disagreed: the participant list was grey while the same
 * person was coloured on their message, and two of the three derived initials differently.</p>
 *
 * <p>Size is a prop rather than a class per surface, because that is the only thing that actually
 * varies between them.</p>
 */
export function MessageAvatar({
  name,
  size = 26,
  className
}: {
  name: string | undefined
  /** Diameter in px. Font scales with it so the initials stay proportional. */
  size?: number
  /** Extra classes for surface-specific layout. Colour and centring stay owned here. */
  className?: string
}) {
  return (
    <span
      className={cn("entity-chat-avatar", className)}
      style={{
        backgroundColor: avatarColorFor(name),
        // Inline, not left to CSS: the participant popover is portaled out of `.entity-chat-panel`, so
        // the ancestor-scoped white rule does not reach it, and the legacy 40px avatar rule sets a muted
        // foreground at equal specificity. Both produced grey-brown initials somewhere. Inline wins over
        // every stylesheet rule short of !important, and nothing marks avatar colour important.
        color: '#fff',
        width: size,
        height: size,
        minWidth: size,
        fontSize: Math.max(8, Math.round(size * 0.4 * 10) / 10)
      }}
      aria-hidden="true"
    >
      {initialsFrom(name)}
    </span>
  )
}
